import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    Tab,
    Tooltip,
    Typography
} from "@mui/material";
import {TabContext} from "@mui/lab";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import EntityActions from "crqlar-react-common/dist/components/EntityActions";
import {useMutation, useQueryClient} from "react-query";
import {enqueueSnackbar} from "notistack";
import {useAllChildResources} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import ResourceDetailsForm from "../resources/ResourceDetailsForm";
import ResourceAvailabilitiesTab from "../resources/ResourceAvailabilitiesTab";
import ResourceServicesTab from "../resources/ResourceServicesTab";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";

interface Props {
    resource: Resource
    open: boolean
    onClose: () => void
    onSaved: (updated: Resource) => void
    onDeleted?: () => void
    onAvailabilitiesChanged?: () => void
}

const ResourceInlineEditDialog = ({resource, open, onClose, onSaved, onDeleted, onAvailabilitiesChanged}: Props) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    // Read-only users may open the resource to view it, but not edit its fields.
    const readOnly = useReadOnly()

    const [activeTab, setActiveTab] = useState<string>('basic')
    const [childrenChangeDetected, setChildrenChangeDetected] = React.useState(false)
    const [childrenChanges, setChildrenChanges] = useState<Map<string, Partial<Resource>>>(new Map())

    useEffect(() => {
        if (open) {
            setActiveTab('basic')
            setChildrenChanges(new Map())
            setChildrenChangeDetected(false)
        }
    }, [open, resource.id])

    const resourceData = useEntity<Resource>({
        get: async () => ({...resource}),
        create: ResourceService.createResource,
        update: async (resourceToUpdate) => {
            const [updatedResource] = await Promise.all([
                ResourceService.updateResource(resourceToUpdate),
                persistChildResourcesState(currentChildResources)
            ])
            return updatedResource
        },
        delete: (id) => ResourceService.deleteResource({id: [id]}),
        isValid: (r) => !!r.name?.trim() && (r.categoryIds?.length ?? 0) > 0,
        additionalDirtyCondition: childrenChangeDetected,
        showSaveSuccess: true,
        onSaved: (saved) => {
            queryClient.invalidateQueries('allRootResources')
            setChildrenChangeDetected(false)
            onSaved(saved)
            onClose()
        },
        onDeleted: () => {
            onDeleted?.()
            onClose()
        },
        cleanupBeforeSave: (r) => {
            return {
                ...r,
                name: r.name?.trim(),
                meta: {
                    ...r.meta,
                    bookableExternally: r.meta?.bookableExternally ?? false,
                    makeAllChildrenBookable: r.meta?.makeAllChildrenBookable ?? false,
                    autoAssignExternally: r.meta?.autoAssignExternally ?? false,
                    seatsAroundTable: r.meta?.seatsAroundTable ?? false
                }
            }
        }
    }, resource.id)

    const r = resourceData.item
    const disabled = !!r.managedExternally || readOnly
    const hasCategory = !!(r.categoryIds?.length)

    const childResources = useAllChildResources(r.id ? [r.id] : [])

    const currentChildResources = useMemo(() =>
        childResources.map(child => {
            const changes = childrenChanges.get(child.id!)
            return changes ? {...child, ...changes} : child
        }),
        [childResources, childrenChanges]
    )

    useEffect(() => {
        setChildrenChangeDetected(childrenChanges?.size > 0)
    }, [childrenChanges])

    const updateField = (field: keyof Resource, value: any) => {
        resourceData.setItem({...r, [field]: value})
    }

    const updateMetaField = (field: string, value: any) => {
        updateField('meta', {...r.meta, [field]: value})
    }

    const updateChildMetaField = (childResource: Resource, field: string, value: any) => {
        setChildrenChanges(prev => {
            const newMap = new Map(prev)
            const existing = newMap.get(childResource.id!) || {}
            const existingMeta = (existing.meta || childResource.meta || {}) as Record<string, any>
            newMap.set(childResource.id!, {...existing, meta: {...existingMeta, [field]: value}})
            return newMap
        })
    }

    const handleRestaurantAutoAssignChange = (autoAssignExternally: boolean) => {
        updateField('meta', {
            ...r.meta,
            autoAssignExternally
        })
        const newChildrenChanges = new Map(childrenChanges)
        currentChildResources.forEach(child => {
            const existing = newChildrenChanges.get(child.id!) || {}
            const existingMeta = (existing.meta || child.meta || {}) as Record<string, any>
            newChildrenChanges.set(child.id!, {...existing, meta: {...existingMeta, autoAssignExternally}})
        })
        setChildrenChanges(newChildrenChanges)
    }

    const handleMakeAllBookable = (bookableExternally: boolean) => {
        updateField('meta', {
            ...r.meta,
            makeAllChildrenBookable: bookableExternally,
            ...(bookableExternally && {bookableExternally: true})
        })
        const newChildrenChanges = new Map(childrenChanges)
        currentChildResources.forEach(child => {
            const existing = newChildrenChanges.get(child.id!) || {}
            const existingMeta = (existing.meta || child.meta || {}) as Record<string, any>
            newChildrenChanges.set(child.id!, {...existing, meta: {...existingMeta, bookableExternally}})
        })
        setChildrenChanges(newChildrenChanges)
    }

    const handleShowSeatsAroundTable = (seatsAroundTable: boolean) => {
        updateField('meta', {...r.meta, seatsAroundTable})
    }

    const resourceMutation = useMutation(
        async (resourcesToUpdate: Resource[]) => ResourceService.updateMulti(resourcesToUpdate),
        {
            onSuccess: (updatedResources: Resource[]) => {
                if (updatedResources) enqueueSnackbar(t('Successfully updated children resources'), {variant: 'success'})
            },
            onError: (error: any) => {
                enqueueSnackbar(t(`An error occurred while updating children resources: ${error}`), {variant: 'error'})
            }
        }
    )

    const persistChildResourcesState = (resources: Resource[]) => {
        const updatedResources = resources
            .map(child => {
                const changes = childrenChanges.get(child.id!)
                return changes ? {...child, ...changes} : null
            })
            .filter(Boolean) as Resource[]
        return updatedResources.length > 0
            ? resourceMutation.mutateAsync(updatedResources)
            : Promise.resolve([])
    }

    const titleSection = (
        <>
            <Stack mb={2} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                <Typography variant={'caption'} color={'text.secondary'}>
                    {t('Last change at', {time: r.updateDate?.toLocaleDateString() || '-'})}
                </Typography>
            </Stack>
            {disabled && (
                <Alert severity={'warning'}>
                    <Typography variant={'body2'}>{t('This entity is managed by an external system and thus cannot be edited here.')}</Typography>
                </Alert>
            )}
        </>
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                // Read-only: block interaction with every field control in any tab (text fields,
                // autocompletes, selects, switches, checkboxes), including raw fields not driven
                // by the `disabled` prop. Tabs and the Cancel button stay usable for viewing.
                sx: readOnly ? {
                    '& .MuiInputBase-root, & .MuiAutocomplete-root, & .MuiSelect-select, & .MuiSwitch-root, & .MuiCheckbox-root, & .MuiRadio-root': {
                        pointerEvents: 'none',
                        opacity: 0.6,
                    },
                } : undefined,
            }}
        >
            <DialogTitle sx={{pb: 0}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <TextField
                        variant="standard"
                        value={r.name || ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        placeholder="-"
                        inputProps={{style: {fontSize: '1.25rem', fontWeight: 'bold'}}}
                        disabled={disabled}
                        sx={{flex: 1, mr: 2}}
                    />
                    <Stack direction="row" gap={1} alignItems="center">
                        <EntityActions data={resourceData}/>
                        <Button variant="outlined" onClick={onClose}>{t('Cancel')}</Button>
                    </Stack>
                </Stack>

                <TabContext value={activeTab}>
                    <ScrollableTabList onChange={(_e, v) => setActiveTab(v)}>
                        <Tab label={t('Basic info')} value={'basic'}/>
                        {hasCategory ? (
                            <Tab label={t('Working/Opening Hours')} value={'availabilities'}/>
                        ) : (
                            <Tooltip title={t('Select a resource category first')} placement="bottom">
                                <span>
                                    <Tab label={t('Working/Opening Hours')} value={'availabilities'} disabled/>
                                </span>
                            </Tooltip>
                        )}
                        <Tab label={t('Services')} value={'services'}/>
                    </ScrollableTabList>
                </TabContext>
            </DialogTitle>

            <DialogContent dividers sx={{p: 0, minHeight: 440}}>
                {activeTab === 'basic' && (
                    <ResourceDetailsForm
                        resourceData={resourceData}
                        titleSection={titleSection}
                        updateField={updateField}
                        updateMetaField={updateMetaField}
                        updateChildMetaField={updateChildMetaField}
                        handleRestaurantAutoAssignChange={handleRestaurantAutoAssignChange}
                        handleMakeAllBookable={handleMakeAllBookable}
                        handleShowSeatsAroundTable={handleShowSeatsAroundTable}
                        currentChildResources={currentChildResources}
                    />
                )}

                {activeTab === 'availabilities' && (
                    <ResourceAvailabilitiesTab
                        resourceId={r.id}
                        disabled={disabled}
                        titleSection={titleSection}
                        onAvailabilitiesChanged={onAvailabilitiesChanged}
                    />
                )}

                {activeTab === 'services' && (
                    <ResourceServicesTab resource={r}/>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default ResourceInlineEditDialog
